import { UserModel } from "@/app/entities";
import { UserTypes, UserTypesCodes, useUsers } from "@/composables/users";
import {
  DashboardHeader,
  UiButton,
  UiInput,
  SideImageCard,
  UiSelect,
  UiSwitch,
  UiAdvancedSelect,
  UiModal,
  UiDatepicker,
} from "@/components";
import { Head } from "@/components/seo";
import { AddEnrolDataModel, useEnrolement } from "@/composables/enrolement";
import { Save, ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { ChangeEvent, useEffect, useState } from "react";
import { format } from "date-fns";
import UserForm from "@/components/molecules/users/user.form";
import { useEnrolementStore } from "@/context/stores/enrolement.store";
import AnimalSuccessCard from "@/components/molecules/enrolement/animal.success.card";

const ENROL_TYPES = ["UNIQUE", "GROUPE"];
const ENROL_TYPES_OPTIONS = [
  {
    value: "",
    label: "Sélectionnez un type d'enrôlement",
  },
  {
    value: "UNIQUE",
    label: "Je souhaite faire un enrolement unique",
  },
  {
    value: "GROUPE",
    label: "Je souhaite faire un enrolement groupe",
  },
];
const ANIMAL_TYPES = [
  "AQUA",
  "BOVIN",
  "CAPRIN",
  "OVIN",
  "PORCIN",
  "VOLAILLE",
  "AUTRE",
];

const ANIMAL_SEXES = [
  { value: "M", label: "MALE" },
  { value: "F", label: "FEMELLE" },
  { value: "N", label: "NEUTRE" },
];

interface OwnerPickerProps {
  defauntValue?: string;
  city?: string;
  onChange: (value: UserModel | null) => void;
}

function OwnerPicker({ onChange, defauntValue, city }: OwnerPickerProps) {
  const [owner, setOwner] = useState<UserModel | null>(null);
  const [fournisseurs, setFournisseurs] = useState<UserModel[]>([]);
  const { loading, getUsers, open, setOpen } = useUsers();

  const doSearch = async (
    options: { page?: number; size?: number } = { size: 1000 }
  ) => {
    const data = await getUsers({
      typeFetch: UserTypesCodes.Terrain,
      page: options.page ? options.page - 1 : 0,
      size: options.size,
    });

    if (city) {
      setFournisseurs(data);
      return;
    }

    setFournisseurs(data);
  };

  useEffect(() => {
    doSearch();
  }, []);

  useEffect(() => {
    setOwner(
      fournisseurs.find((user) => user.matricule === defauntValue) ?? null
    );
  }, [defauntValue]);

  const handleChange = (value: string | null) => {
    const user = fournisseurs.find((user) => user.matricule === value);

    setOwner(user ?? null);
    if (onChange) onChange(user ?? null);
  };

  const onModalClose = () => {
    doSearch();
    setOpen(false);
  };

  return (
    <>
      <div className="w-full flex items-end gap-2">
        <div>
          <UiAdvancedSelect
            className="w-full"
            label="Quel est le propriétaire ?"
            name="matriculeProprietaire"
            loading={loading}
            defaultValue={defauntValue ?? ""}
            placeholder="Choisissez une option"
            options={fournisseurs.map((user) => ({
              value: user.matricule,
              label: `${user.nom} ${user.prenom} (${user.matricule})`,
            }))}
            onChange={handleChange}
          />
        </div>
        <UiButton onClick={() => setOpen(true)}>
          {" "}
          <Plus />
          Ajouter
        </UiButton>
      </div>

      <UiModal
        title="Ajouter un utilisateur"
        onClose={onModalClose}
        visible={open}
        maxWidth="md"
      >
        <UserForm
          initialValue={null}
          onClose={onModalClose}
          authorizedTypes={[UserTypes.PARTICULIER, UserTypes.ENTREPRISE]}
        />
      </UiModal>
    </>
  );
}

// / New StepIndicator Component
interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

function StepIndicator({ currentStep, totalSteps }: StepIndicatorProps) {
  const stepLabels =
    totalSteps === 3
      ? ["Type d'enrôlement", "Propriétaire", "Détails animal"]
      : [
          "Type d'enrôlement",
          "Propriétaire",
          "Sous-lot / Lot",
          "Quantité",
          "Détails animal",
        ];

  return (
    <div className="flex items-center justify-between w-full mb-6 gap-2 flex-wrap">
      {stepLabels.map((label, index) => (
        <div key={index} className="flex items-center flex-1 ">
          {/* Step Circle */}
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
              index < currentStep
                ? "bg-green-500 text-white"
                : index === currentStep
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {index + 1}
          </div>
          {/* Step Label */}
          <div className="ml-2 text-sm font-medium text-gray-700 whitespace-nowrap">
            {label}
          </div>
          {/* Connecting Line */}
          {index < totalSteps - 1 && (
            <div
              className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                index < currentStep ? "bg-green-500" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function DashboardEnrolementsIndex() {
  const { matricule } = useParams<{ matricule: string }>();
  const navigate = useNavigate();
  const { loading, saving, saveEnrolement } = useEnrolement();
  const [step, setStep] = useState(0);
  const [enrolType, setEnrolType] = useState<string>("");
  const [owner, setOwner] = useState<UserModel | null>(null);
  const [sousLot, setSousLot] = useState<boolean>(true);
  const [enrolAll, setEnrolAll] = useState<boolean>(true);
  const [nbSousLot, setNbSousLot] = useState<string>("0");
  const [typeAnimal, setTypeAnimal] = useState<string>("");
  const [espece, setEspece] = useState<string>("");
  const [sexe, setSexe] = useState<string>("");
  const [nom, setNom] = useState<string>("");
  const [poids, setPoids] = useState<number | null>(null);
  const [taille, setTaille] = useState<number | null>(null);
  const [couleur, setCouleur] = useState<string>("");
  const [birthdate, setBirthdate] = useState<Date | undefined>(undefined);
  const [laitiere, setLaitiere] = useState<boolean>(false);
  const [isNextEnabled, setIsNextEnabled] = useState<boolean>(false);
  const [animalFamily, setAnimalFamily] = useState<string>("");
  const [animalData, setAnimalData] = useState<any>(null);

  const enrol = useEnrolementStore((state) => state.enrol);
  const setEnrol = useEnrolementStore((state) => state.setEnrol);

  const getUniqueTypeByAnimalType = (v: string): string[] => {
    switch (v) {
      case "BOVIN":
        return ["VACHE", "TAUREAU", "VEAU", "BUFFLE", "ZEBU"];
      case "CAPRIN":
        return ["CHEVRE", "BOUC"];
      case "OVIN":
        return ["MOUTON", "BREBIS", "AGNEAU"];
      case "PORCIN":
        return ["COCHON", "PORCELET", "TRUIE"];
      case "VOLAILLE":
        return [
          "POULET",
          "PINTADE",
          "CANARD",
          "OIE",
          "DINDE",
          "PIGEON",
          "CAILLE",
          "AUTRUCHE",
          "AUTRE",
        ];
      default:
        return ["CHEVAL", "ANE", "LAPIN", "ABEILLE", "HAMSTER", "AUTRE"];
    }
  };

  const DEFAULTS_STEPS: React.ReactNode[] = [
    <div key="step-1" className="step-content w-full bg-primary">
      <UiSelect
        className="w-full block"
        label="Que souhaitez vous ?"
        name="type"
        defaultValue={enrolType}
        options={ENROL_TYPES_OPTIONS}
        onChange={(v) => {
          setEnrolType(v || "");
        }}
      />
    </div>,

    <div key="step-2" className="step-content">
      <OwnerPicker
        defauntValue={owner ? (owner as UserModel).matricule : ""}
        onChange={(v) => {
          setOwner(v);
        }}
        city={enrol?.infosMarqueur?.ville}
      />
    </div>,

    <div key="step-3" className="step-content">
      <UiSwitch
        label="Souhaitez vous enroler un sous lot ?"
        className="w-full"
        defaultValue={sousLot}
        onChange={() => setSousLot(!sousLot)}
      />
    </div>,

    <div>
      <div key="step-4" className="step-content">
        <UiSwitch
          label={`Souhaitez vous enroler tous le ${
            sousLot ? "sous lot" : "lot"
          } ?`}
          className="w-full"
          defaultValue={enrolAll}
          onChange={handleEnrolAll}
        />
      </div>
      {!enrolAll && (
        <div key="step-5" className="step-content">
          <UiInput
            label="Si non, indiquer le nombre que vous souhaitez enroler"
            placeholder=""
            className="w-full"
            name="nombrePartiel"
            defaultValue={nbSousLot}
            onChange={(v: ChangeEvent<HTMLInputElement>) =>
              setNbSousLot(v.target.value)
            }
          />
        </div>
      )}
    </div>,

    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
      <div key="step-6" className="step-content w-[100%]">
        <UiSelect
          groupClassname="w-full"
          className="w-full"
          label="Animal Type"
          placeholder="Animal Type"
          name="type"
          defaultValue={typeAnimal}
          options={ANIMAL_TYPES.map((type) => ({
            value: type,
            label: type,
          }))}
          onChange={(v) => setTypeAnimal(v || "")}
        />
      </div>

      <div key="step-6" className="step-content w-[100%]">
        <UiSelect
          groupClassname="w-full"
          className="w-full"
          label="Famille de l'animal"
          placeholder="Famille de l'animal"
          name="type"
          defaultValue={animalFamily}
          options={getUniqueTypeByAnimalType(typeAnimal).map((type) => ({
            value: type,
            label: type,
          }))}
          onChange={(v) => setAnimalFamily(v || "")}
        />
      </div>

      <div key="step-7" className="step-content">
        <UiInput
          label="Veuillez entrer l'espèce de l'animal"
          placeholder=""
          className="w-full"
          name="espece"
          defaultValue={espece}
          onChange={(v: ChangeEvent<HTMLInputElement>) =>
            setEspece(v.target.value)
          }
        />
      </div>

      <div key="step-8" className="step-content">
        <UiSelect
          className="w-full"
          label="Quel est le sexe de l'animal ?"
          placeholder="Quel est le sexe de l'animal ?"
          name="sexe"
          defaultValue={sexe}
          options={ANIMAL_SEXES}
          onChange={(v) => setSexe(v || "")}
        />
      </div>
    </div>,
  ];

  const getSteps = () => {
    return enrolType === "UNIQUE"
      ? DEFAULTS_STEPS.filter((item, index) => ![2, 3].includes(index))
      : DEFAULTS_STEPS;
  };

  function handleEnrolAll() {
    setEnrolAll(!enrolAll);
  }

  const goBack = () => {
    if (step === 0) {
      return;
    }

    setStep(step - 1);
  };

  const goNext = () => {
    if (step === 5) {
      return;
    }
    setStep(step + 1);
  };

  const getEnrolData = () => {
    const isUnique = enrolType === "UNIQUE";
    return isUnique
      ? {
          type: animalFamily || "",
          animalType: typeAnimal,
          proprietaire: owner as UserModel,
          matricule: matricule || "",
          sexe: sexe,
          sousLot: false,
          typeEnrolement: enrolType || "",
          nom: nom,
          poids: poids || 0,
          taille: taille || 0,
          couleur: couleur,
          dateNaissance: birthdate ? format(birthdate, "yyyy-MM-dd") : "",
          especeOrigine: espece,
          laitiere: laitiere,
        }
      : {
          type: typeAnimal,
          matriculeProprietaire: (owner as UserModel).matricule,
          matricule: matricule ?? "",
          sexe: sexe,
          espece: animalFamily || "",
          nombrePartiel: enrolAll ? 0 : parseInt(nbSousLot),
          encrypte: true,
          sousLot: sousLot,
          typeEnrolement: enrolType || "",
        };
  };

  const handleSubmit = async () => {
    const isUnique = enrolType === "UNIQUE";
    const params: AddEnrolDataModel = getEnrolData();
    const res = await saveEnrolement(params);

    if (!res) {
      setAnimalData(null);
      return;
    }

    setEnrol(null);
    setAnimalData(res);
    return;
  };

  function validateFormData(): boolean {
    switch (step) {
      case 0:
        return enrolType !== "";
      case 1:
        return owner?.matricule !== "";
      case 2:
        if (enrolType === "UNIQUE") {
          return typeAnimal !== "" && espece !== "" && sexe !== "";
        }
        return true;
      case 3:
        return enrolType !== "UNIQUE";
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  }

  useEffect(() => {
    setIsNextEnabled(validateFormData());
  }, [enrolType, sexe, owner, typeAnimal, espece, step]);

  const handleSaveAgain = () => {
    navigate("/dashboard/enrolements");
  };

  return (
    <>
      <Head title="Enrolements | Jourgui" />
      <DashboardHeader subTitle="Bienvenue sur la page des enrolements" />

      <div className="py-4">
        <SideImageCard
          loading={loading}
          imgSrc="/assets/images/chris-lee-70l1tDAI6rM-unsplash 1.png"
        >
          <div>
            <UiButton
              variant="text"
              onClick={() => navigate(-1)}
              startIcon={<ArrowLeft />}
            >
              Retour
            </UiButton>
          </div>

          <div className="p-4 w-full">
            <h1 className="text-2xl pb-4 form-title">
              Parcours de l'enrolement
            </h1>

            {animalData && (
              <AnimalSuccessCard
                animal={animalData}
                onSaveAgain={handleSaveAgain}
              />
            )}

            {animalData === null && (
              <div>
                {/* Add Step Indicator Here */}
                <StepIndicator
                  currentStep={step}
                  totalSteps={getSteps().length}
                />

                <form action="">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="step-container">{getSteps()[step]}</div>
                    {enrolType === "UNIQUE" && step === 2 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="step-content">
                          <UiInput
                            label="Nom de l'animal"
                            placeholder=""
                            className="w-full"
                            name="nom"
                            defaultValue={nom}
                            onChange={(v: ChangeEvent<HTMLInputElement>) =>
                              setNom(v.target.value)
                            }
                          />
                        </div>

                        <div className="step-content">
                          <UiInput
                            label="Poids de l'animal"
                            placeholder=""
                            className="w-full"
                            name="poids"
                            type="text"
                            defaultValue={poids || ""}
                            onChange={(v: ChangeEvent<HTMLInputElement>) =>
                              setPoids(
                                v.target.value
                                  ? parseFloat(v.target.value)
                                  : null
                              )
                            }
                          />
                        </div>

                        <div className="step-content">
                          <UiInput
                            label="Taille de l'animal"
                            placeholder=""
                            className="w-full"
                            type="number"
                            name="taille"
                            defaultValue={taille || ""}
                            onChange={(v: ChangeEvent<HTMLInputElement>) =>
                              setTaille(
                                v.target.value ? parseInt(v.target.value) : null
                              )
                            }
                          />
                        </div>

                        <div className="step-content">
                          <UiInput
                            label="Couleur de l'animal"
                            placeholder=""
                            className="w-full"
                            name="couleur"
                            defaultValue={couleur}
                            onChange={(v: ChangeEvent<HTMLInputElement>) =>
                              setCouleur(v.target.value)
                            }
                          />
                        </div>

                        <div className="step-content">
                          <UiDatepicker
                            label="Date de naissance de l'animal"
                            placeholder=""
                            className="w-full"
                            name="couleur"
                            defaultValue={birthdate}
                            onChange={(v: Date | null) => {
                              setBirthdate(v ?? undefined);
                            }}
                          />
                        </div>

                        <div key="step-3" className="step-content">
                          <UiSwitch
                            label="Laitière ?"
                            className="w-full"
                            defaultValue={laitiere}
                            onChange={() => setLaitiere(!laitiere)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </form>

                <div className="flex gap-2 flex-wrap items-center justify-between mt-8">
                  <UiButton
                    size="small"
                    className="flex gap-2 items-center"
                    onClick={goBack}
                    disabled={step === 0}
                  >
                    <ArrowLeft />
                  </UiButton>
                  {step < getSteps().length - 1 && (
                    <UiButton
                      size="small"
                      className="flex gap-2 items-center"
                      onClick={goNext}
                      loading={saving}
                      disabled={!isNextEnabled}
                    >
                      <ArrowRight />
                    </UiButton>
                  )}

                  {step === getSteps().length - 1 && (
                    <UiButton
                      size="small"
                      className="flex gap-2 items-center"
                      onClick={handleSubmit}
                      loading={saving}
                      disabled={!isNextEnabled}
                    >
                      <Save />
                    </UiButton>
                  )}
                </div>
              </div>
            )}
          </div>
        </SideImageCard>
      </div>
    </>
  );
}
